import { useMemo } from "react";
import useAPI from "@/services/api/hook";
import { ReportType, SettingsPeriod } from "@/entities/reporting/types";
import { isEmpty } from "lodash";

interface UseDirectReportsCheckResult<TDirectReportsResponse> {
  hasDirectReports: boolean;
  directReportsData?: TDirectReportsResponse;
  isLoading: boolean;
  error: unknown;
}

interface UseDirectReportsCheckConfig {
  reportType: ReportType;
  responseKey: string;
  checkEmptyFn?: (data: any) => boolean;
}

export const useDirectReportsCheck = <TDirectReportsResponse,>(
  userId: string | undefined,
  period: SettingsPeriod,
  enabled: boolean = true,
  config: UseDirectReportsCheckConfig
): UseDirectReportsCheckResult<TDirectReportsResponse> => {
  const { reportType, responseKey, checkEmptyFn } = config;

  const { data, isLoading, error } = useAPI(
    "reports/type/directReports",
    {
      pathParams: { type: reportType.toString() },
      queryParams: {
        period: period.toString(),
        user: userId || ""
      }
    },
    {
      retry: false,
      enabled: enabled && !!userId
    }
  );

  const directReportsData = useMemo(() => {
    return data?.[responseKey]?.directReports as
      | TDirectReportsResponse
      | undefined;
  }, [data, responseKey]);

  const hasDirectReports = useMemo(() => {
    if (!directReportsData) return false;

    // Use custom check function if provided
    if (checkEmptyFn) {
      return checkEmptyFn(directReportsData);
    }

    // Default check: look for directReports array or overall.winRates object
    const dataAsAny = directReportsData as any;

    if (typeof dataAsAny === "object" && dataAsAny !== null) {
      if ("directReports" in dataAsAny) {
        return !isEmpty(dataAsAny.directReports);
      }

      if ("overall" in dataAsAny && dataAsAny.overall?.winRates) {
        return !isEmpty(dataAsAny.overall.winRates);
      }
    }

    return false;
  }, [directReportsData, checkEmptyFn]);

  return {
    hasDirectReports,
    directReportsData,
    isLoading,
    error
  };
};
